import { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, X, Play } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import AuthImage from "@/components/AuthImage";

const NETWORKS = [
  { value: "instagram", label: "Instagram" },
  { value: "facebook", label: "Facebook" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "tiktok", label: "TikTok" },
];
const TYPES = [
  { value: "feed", label: "Feed" },
  { value: "story", label: "Story" },
  { value: "reels", label: "Reels" },
];

export default function EditPostDialog({ open, onOpenChange, post, onSaved }) {
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    day: 1, post_type: "feed", social_networks: ["instagram"],
    copy: "", scheduled_time: "", images: [],
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && post) {
      const nets = post.social_networks && post.social_networks.length
        ? post.social_networks
        : (post.social_network ? [post.social_network] : ["instagram"]);
      setForm({
        day: post.day || 1,
        post_type: post.post_type || "feed",
        social_networks: nets,
        copy: post.copy || "",
        scheduled_time: post.scheduled_time || "",
        images: post.images || [],
      });
    }
  }, [open, post]);

  const toggleNetwork = (value) => {
    setForm((s) => ({
      ...s,
      social_networks: s.social_networks.includes(value)
        ? s.social_networks.filter((x) => x !== value)
        : [...s.social_networks, value],
    }));
  };

  const imagesLocked = post?.status === "approved";

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const uploaded = [];
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post(
          `/upload?client_id=${encodeURIComponent(post.client_id)}&year=${post.year}&month=${post.month}&kind=post`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        uploaded.push({ path: data.path, filename: data.filename, media_type: data.media_type || "image" });
      } catch {
        toast.error(`Erro a carregar ${f.name}`);
      }
    }
    setForm((s) => ({ ...s, images: [...s.images, ...uploaded] }));
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeImage = (idx) => setForm((s) => ({ ...s, images: s.images.filter((_, i) => i !== idx) }));

  const save = async () => {
    setSaving(true);
    try {
      if (form.social_networks.length === 0) {
        toast.error("Selecione pelo menos uma rede social");
        setSaving(false);
        return;
      }
      const payload = {
        day: Number(form.day),
        post_type: form.post_type,
        social_networks: form.social_networks,
        copy: form.copy,
        scheduled_time: form.scheduled_time || null,
      };
      if (!imagesLocked) payload.images = form.images;
      await api.put(`/posts/${post.id}`, payload);
      toast.success("Post atualizado");
      onSaved && onSaved();
      onOpenChange(false);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro ao guardar");
    } finally {
      setSaving(false);
    }
  };

  if (!post) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#F9F6F0] border border-[#EAE3DB] rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl text-[#1A1412]">Editar post</DialogTitle>
        </DialogHeader>
        <div className="space-y-5 mt-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.3em] text-[#3B2B23]">Dia</Label>
              <Input
                type="number" min="1" max="31"
                value={form.day}
                onChange={(e) => setForm({ ...form, day: e.target.value })}
                className="mt-2 h-11 bg-white border-[#EAE3DB]"
                data-testid="edit-post-day-input"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.3em] text-[#3B2B23]">Hora</Label>
              <Input
                type="time"
                value={form.scheduled_time}
                onChange={(e) => setForm({ ...form, scheduled_time: e.target.value })}
                className="mt-2 h-11 bg-white border-[#EAE3DB]"
                data-testid="edit-post-time-input"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.3em] text-[#3B2B23]">Tipo</Label>
              <Select value={form.post_type} onValueChange={(v) => setForm({ ...form, post_type: v })}>
                <SelectTrigger className="mt-2 h-11 bg-white border-[#EAE3DB]" data-testid="edit-post-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-[0.3em] text-[#3B2B23]">Redes sociais</Label>
            <div className="mt-3 flex flex-wrap gap-2">
              {NETWORKS.map((n) => {
                const active = form.social_networks.includes(n.value);
                return (
                  <button
                    key={n.value}
                    type="button"
                    onClick={() => toggleNetwork(n.value)}
                    className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest border transition-all ${
                      active
                        ? "bg-[#3B2B23] text-[#F9F6F0] border-[#3B2B23]"
                        : "bg-white text-[#3B2B23] border-[#EAE3DB] hover:border-[#8B5E3C]"
                    }`}
                    data-testid={`edit-post-network-toggle-${n.value}`}
                  >
                    {n.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-[0.3em] text-[#3B2B23]">Copy</Label>
            <Textarea
              rows={8}
              value={form.copy}
              onChange={(e) => setForm({ ...form, copy: e.target.value })}
              className="mt-2 bg-white border-[#EAE3DB] resize-none"
              data-testid="edit-post-copy-textarea"
            />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label className="text-[10px] uppercase tracking-[0.3em] text-[#3B2B23]">
                Imagens {imagesLocked && <span className="text-[#8A2A22] normal-case tracking-normal ml-2">(bloqueadas — post aprovado)</span>}
              </Label>
            </div>
            <div className="mt-2 flex flex-wrap gap-3">
              {form.images.map((img, idx) => (
                <div key={idx} className="relative w-24 h-24 rounded-xl overflow-hidden border border-[#EAE3DB] bg-white">
                  {img.media_type === "video" ? (
                    <div className="w-full h-full bg-[#3B2B23] flex items-center justify-center">
                      <Play className="w-6 h-6 text-[#F9F6F0]" />
                    </div>
                  ) : (
                    <AuthImage path={img.path} className="w-full h-full object-cover" />
                  )}
                  {!imagesLocked && (
                    <button
                      type="button"
                      onClick={() => removeImage(idx)}
                      className="absolute top-1 right-1 bg-[#3B2B23] text-white rounded-full w-6 h-6 flex items-center justify-center"
                      data-testid={`edit-post-remove-image-${idx}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
              {!imagesLocked && (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="w-24 h-24 rounded-xl border-2 border-dashed border-[#8B5E3C] text-[#5C4A43] flex flex-col items-center justify-center text-[10px] uppercase tracking-widest hover:bg-[#EAE3DB]/40"
                  data-testid="edit-post-upload-button"
                >
                  <Upload className="w-5 h-5 mb-1" />
                  {uploading ? "A enviar…" : "Adicionar"}
                </button>
              )}
              <input ref={fileRef} type="file" accept="image/*,video/*" multiple hidden onChange={handleFiles} />
            </div>
          </div>
        </div>
        <DialogFooter className="pt-6">
          <Button
            type="button" variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-full border-[#3B2B23] text-[#3B2B23] hover:bg-[#EAE3DB]"
            data-testid="edit-post-cancel"
          >
            Cancelar
          </Button>
          <Button
            type="button" disabled={saving}
            onClick={save}
            className="rounded-full bg-[#3B2B23] hover:bg-[#2A1E18] text-[#F9F6F0]"
            data-testid="edit-post-save"
          >
            {saving ? "A guardar…" : "Guardar alterações"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
